/**
 * Admin Configuration tab.
 *
 * Live-edit the MAPS runtime settings that previously lived as hardcoded
 * constants in apps/MJAPI/src/resolvers/maps/rerankConfig.ts. Persistence is
 * via the existing FeedbackPlatform.SystemSetting / "System Settings" entity
 * (maps.* keys); see apps/MJAPI/src/resolvers/maps/settings.ts.
 *
 * Form sections (per the 6-8 client meeting):
 *   - Ranking thresholds (Strong/Medium for both the agent and fallback paths)
 *   - Result set (default count + relevance cutoff)
 *   - AI context (publication-text word budget fed to the LLMs)
 *   - Features (the "Show pipeline panel to partner users" toggle)
 *
 * All edits go through mapsSettingsUpdate; the resolver clamps values to safe
 * ranges and invalidates the 60s settings cache so the next Search picks up
 * the changes immediately.
 */
import { ChangeDetectorRef, Component, Inject, NgZone, OnInit } from '@angular/core';
import { FormBuilder, type FormGroup, Validators } from '@angular/forms';
import { MAPS_HOST_ADAPTER, MapsHostAdapter, MapsView } from '../../../host-adapter';
import { MapsSettingsService } from '../../../services/maps-settings.service';
import type { MapsSettings, MapsSettingsInput } from '../../../models/maps.types';

@Component({
    selector: 'maps-admin-configuration',
    standalone: false,
    templateUrl: './admin-configuration.component.html',
    styleUrl: './admin-configuration.component.scss',
})
export class AdminConfigurationComponent implements OnInit {
    form: FormGroup;
    isLoading = true;
    isSaving = false;
    errorMessage: string | null = null;
    successMessage: string | null = null;

    GoTo(view: MapsView): void {
        this.hostAdapter.NavigateTo(view);
    }

    IsActive(view: MapsView): boolean {
        return this.hostAdapter.CurrentLocation === view;
    }

    constructor(
        private readonly settings: MapsSettingsService,
        @Inject(MAPS_HOST_ADAPTER) private readonly hostAdapter: MapsHostAdapter,
        private readonly fb: FormBuilder,
        private readonly zone: NgZone,
        private readonly cdr: ChangeDetectorRef,
    ) {
        this.form = this.fb.nonNullable.group({
            tierStrong: [0.7, [Validators.required, Validators.min(0), Validators.max(1)]],
            tierMedium: [0.4, [Validators.required, Validators.min(0), Validators.max(1)]],
            defaultResultCount: [50, [Validators.required, Validators.min(1), Validators.max(100)]],
            relevanceCutoff: [0.35, [Validators.required, Validators.min(0), Validators.max(1)]],
            showPipelinePanelToAll: [false, [Validators.required]],
            publicationTextWordLimit: [200, [Validators.required, Validators.min(0), Validators.max(1000)]],
            relevantPublicationCount: [3, [Validators.required, Validators.min(1), Validators.max(10)]],
            careerStageSource: ['personJob', [Validators.required]],
            showCareerStage: [true, [Validators.required]],
            hardDisqualify: [true, [Validators.required]],
            levelDisqualify: [true, [Validators.required]],
            educationDisqualify: [true, [Validators.required]],
            // Preferred-tier qualification weights (positive numbers only).
            // Min 0.1 clamps out zero/negative, which would zero-out or invert
            // the weighting; the resolver clamps to the same [0.1, 100] range.
            // Required quals have no weight — they hard-filter instead.
            qualMultiplierVeryImportant: [3, [Validators.required, Validators.min(0.1), Validators.max(100)]],
            qualMultiplierImportant: [2, [Validators.required, Validators.min(0.1), Validators.max(100)]],
            qualMultiplierNiceToHave: [1, [Validators.required, Validators.min(0.1), Validators.max(100)]],
            // Interdisciplinarity boost pub cap: min 5 = the metric's pub floor
            // (below it every candidate scores neutral); the resolver clamps to
            // the same [5, 100] range. Pair cost is quadratic per person.
            interdiscMaxPubs: [16, [Validators.required, Validators.min(5), Validators.max(100)]],
            // Recruiting-feasibility signal (Test Results 7-15-26): ordering
            // nudge only; off = zero extra LLM calls, pre-feature ordering.
            feasibilityEnabled: [true, [Validators.required]],
            orgCountryInference: [true, [Validators.required]],
            feasibilityWeight: [0.15, [Validators.required, Validators.min(0), Validators.max(1)]],
            // Contact & messaging (2026-08 finish line). Delivery safety: the
            // resolver treats unrecognized modes as dryrun, and 'live' also
            // needs the MAPS_CONTACT_LIVE_ENABLED env flag server-side.
            contactDeliveryMode: ['dryrun', [Validators.required]],
            contactFromAddress: ['', []],
            contactFromName: ['AGU Science Talent Network', [Validators.required]],
            contactRedirectAddress: ['', []],
            contactSendCap: [25, [Validators.required, Validators.min(1), Validators.max(100)]],
            // Rerank provider is intentionally NOT exposed in the form.
            // Controlled backend-only via FeedbackPlatform.SystemSetting
            // (key: maps.rerank.provider). Default: 'cohere'. The contact
            // allowlist + webhook public key are backend-only the same way
            // (maps.contact.recipientAllowlist / maps.contact.webhookPublicKey).
        });
    }

    async ngOnInit(): Promise<void> {
        try {
            const s = await this.settings.Load();
            this.zone.run(() => {
                this.ApplySettings(s);
                this.isLoading = false;
                this.cdr.detectChanges();
            });
        } catch (err) {
            this.HandleError(err, 'Could not Load MAPS settings');
        }
    }

    async Save(): Promise<void> {
        if (this.form.invalid || this.isSaving) return;
        this.isSaving = true;
        this.errorMessage = null;
        this.successMessage = null;
        try {
            // Send the full form payload — even unchanged fields — so the resolver
            // can establish baseline rows on a fresh System Settings table.
            const input: MapsSettingsInput = this.form.getRawValue();
            const saved = await this.settings.Save(input);
            this.zone.run(() => {
                this.ApplySettings(saved);
                this.isSaving = false;
                this.successMessage = 'Settings saved.';
                this.cdr.detectChanges();
            });
        } catch (err) {
            this.HandleError(err, 'Could not Save MAPS settings');
            this.zone.run(() => {
                this.isSaving = false;
                this.cdr.detectChanges();
            });
        }
    }

    private ApplySettings(s: MapsSettings): void {
        this.form.setValue({
            tierStrong: s.tierStrong,
            tierMedium: s.tierMedium,
            defaultResultCount: s.defaultResultCount,
            relevanceCutoff: s.relevanceCutoff,
            showPipelinePanelToAll: s.showPipelinePanelToAll,
            publicationTextWordLimit: s.publicationTextWordLimit,
            relevantPublicationCount: s.relevantPublicationCount,
            careerStageSource: s.careerStageSource,
            showCareerStage: s.showCareerStage,
            hardDisqualify: s.hardDisqualify,
            levelDisqualify: s.levelDisqualify,
            educationDisqualify: s.educationDisqualify,
            qualMultiplierVeryImportant: s.qualMultiplierVeryImportant,
            qualMultiplierImportant: s.qualMultiplierImportant,
            qualMultiplierNiceToHave: s.qualMultiplierNiceToHave,
            interdiscMaxPubs: s.interdiscMaxPubs,
            feasibilityEnabled: s.feasibilityEnabled,
            orgCountryInference: s.orgCountryInference,
            feasibilityWeight: s.feasibilityWeight,
            contactDeliveryMode: s.contactDeliveryMode,
            contactFromAddress: s.contactFromAddress,
            contactFromName: s.contactFromName,
            contactRedirectAddress: s.contactRedirectAddress,
            contactSendCap: s.contactSendCap,
        });
    }

    private HandleError(err: unknown, prefix: string): void {
        const detail =
            (err as { message?: string })?.message ??
            (err as { graphQLErrors?: { message?: string }[] })?.graphQLErrors?.[0]?.message ??
            'unknown error';
        console.error('[maps.admin.configuration]', err);
        this.zone.run(() => {
            this.errorMessage = `${prefix}: ${detail}`;
            this.isLoading = false;
            this.cdr.detectChanges();
        });
    }
}
